import React, { useState, useEffect, useRef } from 'react';
import { PlayingSide, PadelCategory, BeachTennisCategory, Gender, TennisCategory, Sport, Player } from '../types';
import { Camera } from 'lucide-react';
import Modal from './modals/Modal';
import CategoryTooltip from './tooltips/CategoryTooltip';
import { resizeImage } from '../utils/imageUtils';

interface EditProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    phone: string;
    password: string;
    padelCategory?: PadelCategory;
    beachTennisCategory?: BeachTennisCategory;
    tennisCategory?: TennisCategory;
    playingSide: PlayingSide;
    gender: Gender;
    avatar?: string;
    zip_code: string;
  }) => void;
  currentUser: Player;
}

// Tennis categories constant
const tennisCategories: TennisCategory[] = [
  '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'
];

// Padel categories constant
const padelCategories: PadelCategory[] = [
  'CAT 1', 'CAT 2', 'CAT 3', 'CAT 4', 'CAT 5', 'CAT 6'
];

// Beach Tennis categories constant
const beachTennisCategories: BeachTennisCategory[] = [
  'BEGINNER', 'CAT C', 'CAT B', 'CAT A', 'PROFESSIONAL'
];

export default function EditProfileForm({ isOpen, onClose, onSubmit, currentUser }: EditProfileFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '', // Don't pre-fill password for security
    padelCategory: undefined as PadelCategory | undefined,
    beachTennisCategory: undefined as BeachTennisCategory | undefined,
    tennisCategory: undefined as TennisCategory | undefined,
    playingSide: 'both' as PlayingSide,
    gender: 'male' as Gender,
    avatar: '',
    zip_code: '',
    preferredSports: ['padel', 'beach-tennis', 'tennis'] as Sport[],
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update form data when modal opens or currentUser changes
  useEffect(() => {
    if (isOpen && currentUser) {
      setFormData({
        name: currentUser.name || '',
        phone: currentUser.phone || '',
        password: '', // Don't pre-fill password for security
        padelCategory: currentUser.padel_category,
        beachTennisCategory: currentUser.beach_tennis_category,
        tennisCategory: currentUser.tennis_category,
        playingSide: currentUser.playing_side || 'both' as PlayingSide,
        gender: currentUser.gender || 'male' as Gender,
        avatar: currentUser.avatar || '',
        zip_code: currentUser.zip_code || '',
        preferredSports: currentUser.preferred_sports || ['padel', 'beach-tennis', 'tennis'] as Sport[],
      });
      setError(null);
      setImagePreview(currentUser.avatar || null);
    }
  }, [isOpen, currentUser]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      setError('The image must be at most 3MB');
      return;
    }

    try {
      const resizedImage = await resizeImage(file);
      setFormData({ ...formData, avatar: resizedImage });
      setImagePreview(resizedImage);
      setError(null);
    } catch (err) {
      setError('Error processing image');
    }
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 5) value = value.substring(0, 5); // Limit to 5 digits
    setFormData({ ...formData, zip_code: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }
      if (!formData.phone.trim()) {
        setError('Phone is required');
        return;
      }
      if (!formData.zip_code.trim()) {
        setError('ZIP code is required');
        return;
      }
      if (formData.zip_code.length !== 5) {
        setError('ZIP code must be 5 digits');
        return;
      }

      // Check if at least one category is selected
      if (!formData.padelCategory && !formData.beachTennisCategory && !formData.tennisCategory) {
        setError('Please select at least one category');
        return;
      }

      // Prepare submission data
      const submissionData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        password: formData.password, // Include password if provided
        padelCategory: formData.padelCategory,
        beachTennisCategory: formData.beachTennisCategory,
        tennisCategory: formData.tennisCategory,
        playingSide: formData.playingSide,
        gender: formData.gender,
        avatar: formData.avatar,
        zip_code: formData.zip_code,
      };

      await onSubmit(submissionData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Profile"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Profile Picture */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Picture
          </label>
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
              {imagePreview || formData.avatar ? (
                <img
                  src={imagePreview || formData.avatar}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera className="w-8 h-8 text-gray-400" />
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                Choose Image
              </button>
              <p className="text-xs text-gray-500 mt-1">
                The image must be at most 3MB
              </p>
            </div>
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter your name"
          />
        </div>

        {/* Email (read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email (cannot be changed)
          </label>
          <input
            type="email"
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
            value={currentUser.email}
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone *
          </label>
          <input
            type="tel"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        {/* ZIP Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code *
          </label>
          <input
            type="text"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.zip_code}
            onChange={handleZipChange}
            placeholder="12345"
            maxLength={5}
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Password (leave blank to keep current password)
          </label>
          <input
            type="password"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="Enter new password"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Gender *
          </label>
          <div className="flex space-x-4">
            {(['male', 'female', 'other'] as Gender[]).map((gender) => (
              <label key={gender} className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value={gender}
                  checked={formData.gender === gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value as Gender })}
                  className="mr-2"
                />
                <span className="capitalize">{gender}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Playing Side */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Playing Side *
          </label>
          <div className="flex space-x-4">
            {(['left', 'right', 'both'] as PlayingSide[]).map((side) => (
              <label key={side} className="flex items-center">
                <input
                  type="radio"
                  name="playingSide"
                  value={side}
                  checked={formData.playingSide === side}
                  onChange={(e) => setFormData({ ...formData, playingSide: e.target.value as PlayingSide })}
                  className="mr-2"
                />
                <span className="capitalize">{side}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Categories</h3>

          {/* Padel Category */}
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Padel Category
              </label>
              <CategoryTooltip />
            </div>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.padelCategory || ''}
              onChange={(e) => setFormData({ ...formData, padelCategory: e.target.value as PadelCategory || undefined })}
            >
              <option value="">Select Padel Category</option>
              {padelCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Beach Tennis Category */}
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Beach Tennis Category
              </label>
              <CategoryTooltip />
            </div>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.beachTennisCategory || ''}
              onChange={(e) => setFormData({ ...formData, beachTennisCategory: e.target.value as BeachTennisCategory || undefined })}
            >
              <option value="">Select Beach Tennis Category</option>
              {beachTennisCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Tennis Category */}
          <div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Tennis Category
              </label>
              <CategoryTooltip />
            </div>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.tennisCategory || ''}
              onChange={(e) => setFormData({ ...formData, tennisCategory: e.target.value as TennisCategory || undefined })}
            >
              <option value="">Select Tennis Category</option>
              {tennisCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}